import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { obtenerCitas } from '../../api/citaService';
import NuevaCitaModal from './NuevaCitaModal';
import GlobalDataTable from '../common/GlobalDataTable';
import GlobalSearchInput from '../common/GlobalSearchInput';
import { matchesSearchTerm } from '../../utils/globalSearch';

const FETCH_BATCH_SIZE = 100;

const CitasList = () => {
  const navigate = useNavigate();
  const [citas, setCitas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [filtroPago, setFiltroPago] = useState('todos');
  const [busqueda, setBusqueda] = useState('');
  const [showNuevaCita, setShowNuevaCita] = useState(false);

  const cargarCitas = useCallback(async () => {
    try {
      setIsLoading(true);

      const response = await obtenerCitas({ page: 1, limit: FETCH_BATCH_SIZE });
      console.log('Respuesta de obtenerCitas:', response);

      const extraerCitas = (payload) => {
        if (payload?.data?.data?.citas) return payload.data.data.citas;
        if (payload?.data?.citas) return payload.data.citas;
        if (Array.isArray(payload?.data)) return payload.data;
        return [];
      };

      const meta = response?.data?.data?.meta || response?.data?.meta || null;
      const totalPages = Math.max(1, meta?.pages || 1);

      const paginas = [extraerCitas(response)];

      for (let page = 2; page <= totalPages; page += 1) {
        const responsePage = await obtenerCitas({ page, limit: FETCH_BATCH_SIZE });
        paginas.push(extraerCitas(responsePage));
      }

      const citasData = paginas.flat();
      
      // Procesar y normalizar los datos de las citas
      const citasProcesadas = citasData.map(cita => ({
        ...cita,
        fecha: cita.fecha ? new Date(cita.fecha) : new Date(),
        estado: cita.estado?.toLowerCase() || 'pendiente',
        pago: cita.pago ? {
          ...cita.pago,
          estado: cita.pago.estado?.toLowerCase() || 'pendiente',
          metodo: cita.pago.metodo?.toLowerCase() || 'efectivo'
        } : {
          estado: 'pendiente',
          metodo: 'efectivo'
        },
        servicio: cita.servicio ? {
          ...cita.servicio,
          titulo: cita.servicio.nombre || cita.servicio.titulo || 'Servicio'
        } : {
          titulo: 'Servicio sin definir'
        }
      }));
      
      setCitas(citasProcesadas);
      setError(null);
    } catch (err) {
      console.error('Error al cargar citas:', err);
      setError('No se pudieron cargar las citas. Por favor, intente de nuevo más tarde.');
      
      // Para desarrollo, usar datos simulados cuando falla la conexión
      setCitas([
        {
          id: '1',
          paciente: {
            nombre: 'Juan',
            apellido: 'Pérez',
            cedula: '1234567890',
            email: 'juan@example.com'
          },
          servicio: {
            titulo: 'Consulta General'
          },
          fecha: new Date(),
          estado: 'pendiente',
          pago: {
            estado: 'pendiente',
            metodo: 'efectivo'
          }
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarCitas();
  }, [cargarCitas]);

  useEffect(() => {
    const handleDashboardAction = (event) => {
      if (event?.detail?.action === 'nueva-cita') {
        setShowNuevaCita(true);
      }
    };

    window.addEventListener('dashboard-action', handleDashboardAction);
    return () => window.removeEventListener('dashboard-action', handleDashboardAction);
  }, []);
  
  // Aplicar filtros
  const citasFiltradas = citas.filter(cita => {
    // Filtrar por estado
    if (filtroEstado !== 'todos' && cita.estado !== filtroEstado) return false;
    
    // Filtrar por estado de pago
    if (filtroPago !== 'todos' && cita.pago.estado !== filtroPago) return false;
    
    // Filtrar por búsqueda
    if (busqueda) {
      return matchesSearchTerm(
        busqueda,
        `${cita.paciente?.nombre || ''} ${cita.paciente?.apellido || ''}`,
        cita.paciente?.cedula,
        cita.paciente?.email
      );
    }
    
    return true;
  });
  
  // Obtener color según estado
  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'confirmada': return { bg: '#e8f5e9', color: '#4caf50' };
      case 'pendiente': return { bg: '#fff3e0', color: '#ff9800' };
      case 'completada': return { bg: '#e1f5fe', color: '#03a9f4' };
      case 'cancelada': return { bg: '#ffebee', color: '#f44336' };
      default: return { bg: '#f5f5f5', color: '#757575' };
    }
  };
  
  // Obtener color según estado de pago
  const getPagoColor = (estado) => {
    switch (estado) {
      case 'completado': return { bg: '#e8f5e9', color: '#4caf50' };
      case 'pendiente': return { bg: '#fff3e0', color: '#ff9800' };
      case 'reembolsado': return { bg: '#ffebee', color: '#f44336' };
      default: return { bg: '#f5f5f5', color: '#757575' };
    }
  };

  const columnasCitas = [
    {
      key: 'idCita',
      header: 'ID',
      render: (cita) => <strong>{formatNumeroCita(cita.numero)}</strong>,
    },
    {
      key: 'paciente',
      header: 'Paciente',
      render: (cita) => (
        <div>
          <div style={{ fontWeight: 700 }}>
            {cita.paciente?.nombre || ''} {cita.paciente?.apellido || ''}
          </div>
          <div className="global-table-subtext">{cita.paciente?.email || ''}</div>
        </div>
      ),
    },
    {
      key: 'servicio',
      header: 'Servicio',
      render: (cita) => cita.servicio?.titulo || 'Sin definir',
    },
    {
      key: 'fechaHora',
      header: 'Fecha y Hora',
      render: (cita) => (
        cita.fecha?.toLocaleString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }) || 'Fecha no definida'
      ),
    },
    {
      key: 'estado',
      header: 'Estado',
      render: (cita) => {
        const estadoColor = getEstadoColor(cita.estado);
        return (
          <span className="global-badge" style={{ backgroundColor: estadoColor.bg, color: estadoColor.color }}>
            {cita.estado.charAt(0).toUpperCase() + cita.estado.slice(1)}
          </span>
        );
      },
    },
    {
      key: 'pago',
      header: 'Pago',
      render: (cita) => {
        const pagoColor = getPagoColor(cita.pago.estado);
        const metodo = cita.pago.metodo === 'tarjeta'
          ? 'Tarjeta'
          : cita.pago.metodo === 'transferencia'
            ? 'Transferencia'
            : 'Efectivo';

        return (
          <div>
            <span className="global-badge" style={{ backgroundColor: pagoColor.bg, color: pagoColor.color }}>
              {cita.pago.estado.charAt(0).toUpperCase() + cita.pago.estado.slice(1)}
            </span>
            <div className="global-table-subtext">{metodo}</div>
          </div>
        );
      },
    },
    {
      key: 'acciones',
      header: 'Acciones',
      align: 'center',
      render: (cita) => (
        <div className="global-table-actions">
          <button
            onClick={() => handleVerDetalle(cita.id, formatNumeroCita(cita.numero))}
            className="global-table-btn global-table-btn--info"
          >
            <i className="fas fa-info-circle"></i>
            Detalles
          </button>

          {cita.paciente?.cedula && (
            <Link to={`/dashboard/historia-clinica/${cita.paciente.cedula}`} className="global-table-btn global-table-btn--primary">
              <i className="fas fa-file-medical"></i>
              Historia
            </Link>
          )}
        </div>
      ),
    },
  ];
  
  // Manejar la navegación al detalle de la cita
  const handleVerDetalle = (citaId, citaNumero) => {
    navigate(`/dashboard/citas/${citaId}`, {
      state: {
        citaNumero,
      },
    });
  };

  const formatNumeroCita = (numero) => {
    if (typeof numero !== 'number') return '-';
    return `C-${String(numero).padStart(4, '0')}`;
  };

  const handleCitaCreada = async () => {
    setShowNuevaCita(false);
    await cargarCitas();
  };
  
  return (
    <div style={{ padding: '20px' }}>
      <NuevaCitaModal
        isOpen={showNuevaCita}
        onClose={() => setShowNuevaCita(false)}
        onCreated={handleCitaCreada}
      />
      <h2 style={{ marginBottom: '20px' }}>Lista de Citas</h2>
      
      {/* Filtros y búsqueda */}
      <div style={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: '15px', 
        marginBottom: '20px',
        alignItems: 'center'
      }}>
        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#666' }}>
            Estado de cita:
          </label>
          <select 
            value={filtroEstado} 
            onChange={(e) => setFiltroEstado(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '4px',
              border: '1px solid #ddd',
              backgroundColor: 'white'
            }}
          >
            <option value="todos">Todos</option>
            <option value="pendiente">Pendientes</option>
            <option value="confirmada">Confirmadas</option>
            <option value="completada">Completadas</option>
            <option value="cancelada">Canceladas</option>
          </select>
        </div>
        
        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#666' }}>
            Estado de pago:
          </label>
          <select 
            value={filtroPago} 
            onChange={(e) => setFiltroPago(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '4px',
              border: '1px solid #ddd',
              backgroundColor: 'white'
            }}
          >
            <option value="todos">Todos</option>
            <option value="pendiente">Pendientes</option>
            <option value="completado">Completados</option>
            <option value="reembolsado">Reembolsados</option>
          </select>
        </div>
        
        <div style={{ flex: 1, minWidth: '250px' }}>
          <GlobalSearchInput
            label="Buscar paciente:"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Nombre, cédula o email"
          />
        </div>
      </div>
      
      {/* Estado de carga */}
      {isLoading && (
        <div style={{ 
          padding: '30px', 
          textAlign: 'center', 
          backgroundColor: 'white',
          borderRadius: '10px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <p>Cargando citas...</p>
        </div>
      )}
      
      {/* Mensaje de error */}
      {error && (
        <div style={{ 
          padding: '30px', 
          textAlign: 'center', 
          backgroundColor: 'white',
          borderRadius: '10px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          color: '#f44336'
        }}>
          <p>{error}</p>
        </div>
      )}
      
      {/* Tabla de citas */}
      {!isLoading && !error && (
        <GlobalDataTable
          columns={columnasCitas}
          rows={citasFiltradas}
          rowKey="id"
          emptyMessage="No se encontraron citas que coincidan con los filtros"
          emptyIcon={<i className="fas fa-calendar-times"></i>}
          rowsPerPage={10}
          pageResetKey={`${busqueda}|${filtroEstado}|${filtroPago}`}
        />
      )}
    </div>
  );
};

export default CitasList;